import React from "react";
import { formatDateInTZ } from "../../lib/timezone";
import type { LeadTimelineEvent } from "../../types";
import { toDisplayCurrency } from "../../utils/formatters";

// Shared event labels + dot colors for the Lead Journey column.
// Used by LostPreCallsModal + OrphanPreCallsModal.
export const EVENT_LABELS: Record<string, string> = {
  webinar_signup: "Webinar Signup",
  typeform_submission: "Typeform",
  pre_call_submission: "Pre-Call Form",
  sales_pre_call: "Pre-Call Form",
  form_submission: "Form Submission",
  low_ticket_purchase: "Low Ticket",
  call_booked: "Call Booked",
  call_cancelled: "Call Cancelled",
  sales_call_outcome: "Sales Call",
  paying_installment: "Installment",
};

export const EVENT_DOT: Record<string, string> = {
  webinar_signup: "bg-purple-500",
  typeform_submission: "bg-purple-500",
  pre_call_submission: "bg-purple-500",
  sales_pre_call: "bg-purple-500",
  form_submission: "bg-purple-500",
  low_ticket_purchase: "bg-indigo-500",
  call_booked: "bg-blue-500",
  call_cancelled: "bg-amber-500",
  sales_call_outcome: "bg-emerald-500",
  paying_installment: "bg-emerald-500",
};

// Call-type events use call_start_time per CLAUDE.md attribution rule;
// others use event_date.
export function eventDate(e: LeadTimelineEvent): string | null {
  if (e.event_type === "call_booked" || e.event_type === "call_cancelled") {
    return e.call_start_time || e.call_booked_time || e.event_date;
  }
  return e.event_date;
}

// Keys that the compact summary already shows or that aren't useful to the reader.
const HIDDEN_CUSTOM_DATA_KEYS = new Set([
  "closer_name",
  "setter_name",
  "invitee_name",
  "invitee_email",
  "google_form_response_id",
  "fathom_link",
  "a1",
  "a2",
  "a3",
  "a4",
  "a5",
  "a6",
  "a7",
  "a8",
  "a9",
  "a10",
]);

// Pretty-cased key (`context_notes` → `Context Notes`).
function prettyKey(k: string): string {
  return k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\s+/g, " ");
}

function formatValue(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v.trim() || "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return JSON.stringify(v);
}

// -----------------------------------------------------------------------------
// Per-event-type expanded content blocks
// -----------------------------------------------------------------------------

function PreCallDetails({ event }: { event: LeadTimelineEvent }) {
  const cd = event.custom_data || {};
  const rows: Array<[string, unknown]> = [
    ["Setter", event.setter_name],
    ["Priority", cd.priority_level],
    ["Money read (1-10)", cd.money_score],
    ["Budget (lead's words)", cd.investment_amount === "-" ? null : cd.investment_amount],
    ["Platform", cd.platform_source || cd.source_platform],
    ["Context Notes", cd.context_notes],
  ].filter(([, v]) => v != null && v !== "") as Array<[string, unknown]>;

  return rows.length === 0 ? (
    <div className="token-10 text-glass-inactive italic">No pre-call details captured.</div>
  ) : (
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="token-10u uppercase tracking-wide text-glass-muted">{k}</dt>
          <dd className="token-11 text-glass whitespace-pre-wrap">{formatValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function CallBookedDetails({ event, timezone }: { event: LeadTimelineEvent; timezone: string }) {
  const cd = event.custom_data || {};
  const slotDisplay = event.call_start_time
    ? formatDateInTZ(event.call_start_time, timezone, { short: false })
    : null;

  const rows: Array<[string, unknown]> = [
    ["Calendar", event.status],
    ["Closer", event.closer_name],
    ["Slot", slotDisplay],
    ["Invitee", cd.invitee_name],
    ["Email", cd.invitee_email],
  ].filter(([, v]) => v != null && v !== "") as Array<[string, unknown]>;

  return (
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="token-10u uppercase tracking-wide text-glass-muted">{k}</dt>
          <dd className="token-11 text-glass">{formatValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function CallCancelledDetails({ event }: { event: LeadTimelineEvent }) {
  return (
    <div className="space-y-2">
      <div>
        <div className="token-10u uppercase tracking-wide text-glass-muted mb-0.5">
          Cancelled by
        </div>
        <span className="token-10 px-1.5 py-0.5 rounded bg-ds-amber/15 text-ds-amber font-medium">
          {event.call_cancel_by || "Unknown"}
        </span>
      </div>
      <div>
        <div className="token-10u uppercase tracking-wide text-glass-muted mb-0.5">Reason</div>
        <div
          className={`token-11 ${
            event.call_cancel_reason ? "text-glass italic" : "text-glass-inactive italic"
          }`}
        >
          {event.call_cancel_reason
            ? `"${event.call_cancel_reason}"`
            : "Not captured by Calendly — check the chat for context"}
        </div>
      </div>
    </div>
  );
}

function SalesCallDetails({ event }: { event: LeadTimelineEvent }) {
  const cd = event.custom_data || {};
  const rows: Array<[string, unknown]> = [
    ["Outcome", event.status],
    ["Closer", event.closer_name],
    ["Setter", event.setter_name],
    ["Program", event.sales_program_sold],
    [
      "Cash Collected",
      event.cash_collected > 0
        ? `$${toDisplayCurrency(event.cash_collected).toLocaleString()}`
        : null,
    ],
    [
      "Revenue Closed",
      event.revenue_closed > 0
        ? `$${toDisplayCurrency(event.revenue_closed).toLocaleString()}`
        : null,
    ],
    ["Quality of Lead", cd.quality_of_lead],
    ["Source Platform", cd.source_platform],
    ["Outcome Notes", cd.outcome_notes],
  ].filter(([, v]) => v != null && v !== "") as Array<[string, unknown]>;

  return (
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="token-10u uppercase tracking-wide text-glass-muted">{k}</dt>
          <dd className="token-11 text-glass whitespace-pre-wrap">{formatValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function GenericCustomDataDetails({ event }: { event: LeadTimelineEvent }) {
  const cd = event.custom_data || {};
  const entries = Object.entries(cd).filter(
    ([k, v]) => !HIDDEN_CUSTOM_DATA_KEYS.has(k) && v != null && v !== ""
  );
  const extras: Array<[string, unknown]> = [];
  if (event.web_signup_source) extras.push(["Source", event.web_signup_source]);
  if (event.status) extras.push(["Status", event.status]);

  const rows = [...extras, ...entries];

  return rows.length === 0 ? (
    <div className="token-10 text-glass-inactive italic">No additional details captured.</div>
  ) : (
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={String(k)}>
          <dt className="token-10u uppercase tracking-wide text-glass-muted">
            {prettyKey(String(k))}
          </dt>
          <dd className="token-11 text-glass whitespace-pre-wrap">{formatValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ExpandedDetails({ event, timezone }: { event: LeadTimelineEvent; timezone: string }) {
  switch (event.event_type) {
    case "sales_pre_call":
    case "pre_call_submission":
      return <PreCallDetails event={event} />;
    case "call_booked":
      return <CallBookedDetails event={event} timezone={timezone} />;
    case "call_cancelled":
      return <CallCancelledDetails event={event} />;
    case "sales_call_outcome":
      return <SalesCallDetails event={event} />;
    default:
      return <GenericCustomDataDetails event={event} />;
  }
}

// -----------------------------------------------------------------------------
// Main card
// -----------------------------------------------------------------------------

interface LeadJourneyEventCardProps {
  event: LeadTimelineEvent;
  expanded: boolean;
  onToggle: () => void;
  timezone: string;
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-3 h-3 text-glass-inactive transition-transform shrink-0 ${
      open ? "rotate-90" : ""
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

export const LeadJourneyEventCard: React.FC<LeadJourneyEventCardProps> = ({
  event,
  expanded,
  onToggle,
  timezone,
}) => {
  const date = eventDate(event);
  const label = EVENT_LABELS[event.event_type] || event.event_type;
  const dotClass = EVENT_DOT[event.event_type] || "bg-slate-500";

  return (
    <div className="relative pl-6">
      <div
        className={`absolute left-0 top-1.5 w-[13px] h-[13px] rounded-full border-2 border-black/[0.1] ${dotClass} shadow-sm`}
      />
      <div
        className="liquid-glass-light px-3 py-2 rounded-lg cursor-pointer hover:bg-black/[0.02] transition-colors"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Chevron open={expanded} />
            <span className="token-12 font-medium text-glass truncate">{label}</span>
          </div>
          {date && (
            <span className="token-10 text-glass-muted shrink-0">
              {formatDateInTZ(date, timezone, { short: true })}
            </span>
          )}
        </div>

        {/* Compact summary — always visible */}
        {event.status && (
          <div className="token-10 text-glass-muted">
            {event.status}
            {event.cash_collected > 0 &&
              ` · $${toDisplayCurrency(event.cash_collected).toLocaleString()}`}
          </div>
        )}
        {event.closer_name && (
          <div className="token-10 text-glass-muted mt-1">Closer: {event.closer_name}</div>
        )}
        {event.sales_program_sold && (
          <div className="token-10 text-emerald-500 mt-0.5">{event.sales_program_sold}</div>
        )}

        {/* Expanded — type-specific details */}
        {expanded && (
          <div className="mt-2 pt-2 border-t border-black/[0.05]">
            <ExpandedDetails event={event} timezone={timezone} />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadJourneyEventCard;
